import _ from 'lodash';
import {getLocaleNamespace, PageMap, Topology} from '@wix/wixstores-client-core';
import {IDataResponse} from '../../types/cart';
import {PubSubManager, SiteStore} from '@wix/wixstores-client-storefront-sdk';
import type {
  EcomPlatformViewerScriptContext,
  IEcomPlatformPublicApiInternal,
} from '@wix/ecom-platform-viewer-script/internal';
import {isWorker} from '@wix/ecom-platform-viewer-script/internal';
import {ICartIconStyleParams, ICtrlProps} from '../../types/app-types';
import {query as getAppSettingsData} from '../../graphql/getAppSettingsData.graphql';
import {IControllerConfig} from '@wix/native-components-infra/dist/es/src/types/types';
import {EMPTY_CART_GUID, FedopsInteraction, SPECS} from '../../constants';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {CartModel} from '../models/Cart.model';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {IPubSub, IWebStorage} from '@wix/native-components-infra/dist/src/types/types';
import {BIService} from '../services/BIService';

const iconsWithText = [3, 4, 5, 6, 7];

export class CartIconStore {
  private appSettingsPromise?: Promise<any>;
  private readonly pubSubManager: PubSubManager;
  private appSettings?: {[key: string]: any};
  private styleParams: ICartIconStyleParams;
  private cartModel?: CartModel;

  constructor(
    private readonly siteStore: Omit<SiteStore, 'experiments'>,
    private readonly viewerContext: ViewerContext,
    private readonly config: IControllerConfig,
    private readonly setProps: (props: any) => void,
    private readonly reportError: (e: any) => any,
    private readonly translations: ControllerParams['flowAPI']['translations'],
    private readonly fedops: ControllerParams['flowAPI']['fedops'],
    private readonly panoramaClient: ControllerParams['flowAPI']['panoramaClient'],
    private readonly currentCartService: EcomPlatformViewerScriptContext['currentCartService'],
    private readonly biService: BIService,
    private readonly memoryStorage: IWebStorage,
    pubSub: IPubSub
  ) {
    this.pubSubManager = new PubSubManager(pubSub);
    this.styleParams = this.config.style.styleParams;

    this.currentCartService.onChange(() => {
      void this.onUpdated();
    });
  }

  private async onUpdated() {
    try {
      await this.fetchCart();
      this.updateCartCount(this.cartModel!.itemsCount);
    } catch (e) {
      /* istanbul ignore next */
      this.reportError(e);
    }
  }

  public updateCartCount(count: number | undefined): void {
    this.setProps({
      ...this.getCountRelatedProps(count),
    });
  }

  private readonly isMultilingualNonPrimary = () => {
    return !this.viewerContext.isPrimaryLanguage();
  };

  public async init(): Promise<void> {
    const cartPromise: Promise<void> | undefined = this.viewerContext.isSSR()
      ? /* istanbul ignore next: todo: missing test */ undefined
      : this.fetchCart();

    const shouldFetchAppSettingsML = this.isMultilingualNonPrimary();

    const shouldFetchAppSettingsNeededIcon = iconsWithText.includes(this.styleParams.numbers.cartWidgetIcon || 1);

    const shouldFetchAppSettingsExternalIdDefined = !!this.config.externalId;

    const appSettingsPromise =
      shouldFetchAppSettingsML && shouldFetchAppSettingsNeededIcon && shouldFetchAppSettingsExternalIdDefined
        ? this.getAppSettingsData()
        : undefined;

    try {
      const [serverResponse, url] = await Promise.all([
        appSettingsPromise,
        this.viewerContext.getSectionUrl(PageMap.CART),
      ]);
      const props = {
        ...this.getCountRelatedProps(undefined),
        cartLink: url ?? '',
        fitToContentHeight: true,
        isInteractive: !this.viewerContext.isSSR(),
        isLoaded: true,
        displayText: this.getDisplayText(serverResponse?.widgetSettings),
        triggerFocus: false,
        onFocusTriggered: this.onFocusTriggered,
        isNavigate: !this.shouldOpenSideCartFromSettings(),
        onIconClick: this.onIconClick,
        onIconHover: this.onIconHover,
        onAppLoaded: this.onAppLoaded,
        ravenUserContextOverrides: {
          id: this.viewerContext.getEcomPlatformInstanceId(),
          uuid: this.viewerContext.getVisitorId(),
        },
        fixCartIconOnEditor: this.viewerContext.isExperimentEnabled(SPECS.FixCartIconOnEditor),
        fixCartCountOverlap: this.viewerContext.isExperimentEnabled(SPECS.FixCartCountOverlap),
        updateCartIconsSVGViewer: this.viewerContext.isExperimentEnabled(SPECS.UpdateCartIconsSVGViewer),
      } as ICtrlProps;
      this.setProps(props);

      cartPromise
        ?.then(() => {
          this.fedops.interactionEnded(FedopsInteraction.CART_ICON_LOADED);
          this.setProps({
            ...this.getCountRelatedProps(this.cartModel?.itemsCount),
          });
        })
        .catch((e) => {
          /* istanbul ignore next: todo: missing test */
          this.cartModel = new CartModel({id: EMPTY_CART_GUID, itemsCount: 0});
          this.updateCartCount(0);
          this.reportError(e);
        });
    } catch (e) {
      /* istanbul ignore next: todo: missing test */
      this.reportError(e);
    }
  }

  /* istanbul ignore next: todo: missing test */
  public onFocusTriggered = (): void => {
    this.setProps({
      triggerFocus: false,
    });
  };

  public onAppLoaded = (): void => {
    /* istanbul ignore else: todo: missing test */
    if (!isWorker() || !this.viewerContext.isSSR()) {
      const shouldReport = this.memoryStorage.getItem('cartIconLoaded');
      /* istanbul ignore else: todo: missing test */
      if (!shouldReport) {
        this.memoryStorage.setItem('cartIconLoaded', 'true');
        void this.reportCartIconLoaded();
      }
    }
  };

  private readonly reportCartIconLoaded = async (): Promise<void> => {
    this.biService.reportCartIconLoaded({
      cartId: await this.getCartId(),
      shouldOpenSideCartFromSettings: this.shouldOpenSideCartFromSettings(),
    });
  };

  public getDisplayText(widgetSettings?: {[key: string]: string}): string {
    const defaultValue = this.translations.t(`CART_ICON_${this.styleParams.numbers.cartWidgetIcon || 1}`);
    let widgetSettingsForLocale = {};
    if (this.isMultilingualNonPrimary()) {
      /* istanbul ignore else: todo: missing test */
      if (widgetSettings) {
        widgetSettingsForLocale = widgetSettings[getLocaleNamespace(this.viewerContext.getCurrentLanguage())];
        return _.get(widgetSettingsForLocale, 'CART_ICON_TEXT', '') || defaultValue;
      } else if (this.appSettings) {
        widgetSettingsForLocale = this.appSettings[getLocaleNamespace(this.viewerContext.getCurrentLanguage())];
        return _.get(widgetSettingsForLocale, 'CART_ICON_TEXT', '') || defaultValue;
      } else {
        return defaultValue;
      }
    }
    return (
      _.get(this.appSettings, 'main.CART_ICON_TEXT', '') ||
      _.get(this.config, 'publicData.APP.CART_ICON_TEXT', '') ||
      _.get(this.config, 'publicData.COMPONENT.CART_ICON_TEXT', '') ||
      defaultValue
    );
  }

  /* istanbul ignore next: todo: missing test */
  public updateStyleParams(newStyleParams: ICartIconStyleParams, publicData?: IControllerConfig['publicData']) {
    this.styleParams = newStyleParams;
    publicData && (this.config.publicData = publicData);
    this.setProps({
      displayText: this.getDisplayText(),
    });
  }

  /* istanbul ignore next: todo: missing test */
  public updateAppSettings(appSettings: {[key: string]: any}) {
    this.appSettings = appSettings;
    this.setProps({
      displayText: this.getDisplayText(),
    });
  }

  public async getAppSettingsData(): Promise<IDataResponse> {
    /* istanbul ignore next: todo: missing test */
    if (this.appSettingsPromise) {
      return this.appSettingsPromise;
    }

    const postData = {
      query: getAppSettingsData,
      source: 'WixStoresWebClient',
      operationName: 'getAppSettings',
      variables: {externalId: this.config.externalId || /* istanbul ignore next: todo: missing test */ ''},
    };

    this.appSettingsPromise = this.siteStore
      .tryGetGqlAndFallbackToPost(this.siteStore.resolveAbsoluteUrl(`/${Topology.STOREFRONT_GRAPHQL_URL}`), postData)
      .then(({data}) => {
        return {
          widgetSettings: _.get(data, 'appSettings.widgetSettings', {}),
        };
      });

    return this.appSettingsPromise;
  }

  public shouldOpenSideCartFromSettings(): boolean {
    const {iconLink} = this.styleParams.numbers;
    return !iconLink || iconLink === 2;
  }

  private readonly handleNavigateToCart = async (cartId?: string) => {
    const origin = 'cart-icon';

    this.biService.reportClickToNavigateToCart({cartId, itemsCount: this.cartModel?.itemsCount, origin});

    const queryParams: {[key: string]: string} = {appSectionParams: JSON.stringify({origin})};
    await this.viewerContext.navigateToSection(PageMap.CART, queryParams);
  };

  private readonly subscribeToMiniCartCloseEvent = () => {
    /* istanbul ignore next: todo: missing test */
    const eventId = this.pubSubManager.subscribe('Minicart.DidClose', () => {
      this.setProps({
        triggerFocus: true,
      });
      /* istanbul ignore next: todo: missing test */
      this.pubSubManager.unsubscribe('Minicart.DidClose', eventId);
    });
  };
  public onIconClick = async (): Promise<void> => {
    const cartId = await this.getCartId();
    if (this.shouldOpenSideCartFromSettings()) {
      const api: IEcomPlatformPublicApiInternal = await this.viewerContext.getEcomPlatformPublicAPI();

      try {
        await Promise.resolve(api.cart.openSideCart());
      } catch (err) {
        /* istanbul ignore next: todo: missing test */
        await this.handleNavigateToCart(cartId);
        /* istanbul ignore next: todo: missing test */
        return;
      }

      this.biService.reportClickToOpenSideCart({cartId, itemsCount: this.cartModel?.itemsCount});

      if (!(await api.cart.hasSideCart())) {
        this.subscribeToMiniCartCloseEvent();
      }
    } else {
      await this.handleNavigateToCart(cartId);
    }
  };

  public onIconHover = async (): Promise<void> => {
    const api = await this.viewerContext.getEcomPlatformPublicAPI();

    if (this.shouldOpenSideCartFromSettings()) {
      await api.cart.preloadSideCart();
    } else {
      await api.cart.preloadCartPage();
    }
  };

  /* istanbul ignore next: todo: missing test */
  public unSubscribeAll(): void {
    return this.pubSubManager.unsubscribeAll();
  }

  private async getCartId(): Promise<string | undefined> {
    let id = this.cartModel?.id;

    if (!id) {
      /* istanbul ignore else: todo: missing test */
      try {
        await this.fetchCart();
        id = this.cartModel?.id;
      } catch (e) {
        /* istanbul ignore next: todo: missing test */
        id = EMPTY_CART_GUID;
      }
    }

    return id === EMPTY_CART_GUID ? undefined : id;
  }

  private getCountRelatedProps(count: number | undefined) {
    return {
      count,
      ariaLabelLink: this.translations.t('sr.CART_WIDGET_BUTTON_TEXT', {itemsCount: count}),
    };
  }

  private async fetchCart(): Promise<void> {
    if (this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)) {
      const cart = await this.currentCartService.getCurrentCart();
      this.cartModel = CartModel.fromCartV2(cart);
    } else {
      const cart = await this.currentCartService.getCurrentCartV1();
      this.cartModel = CartModel.fromCartV1(cart);
    }
  }

  public async executeWithFedops(interaction: FedopsInteraction, fn: () => Promise<any>): Promise<void> {
    this.fedops.interactionStarted(interaction);
    this.panoramaClient?.transaction(interaction).start();
    await fn();
    this.fedops.interactionEnded(interaction);
    this.panoramaClient?.transaction(interaction).finish();
  }
}
